import * as vp from 'vopay';
import {
  Table,
  Box,
  Pagination,
  GetColorStatus,
  capitalizeFirstLetter,
  getMaskedNumber,
  usePagination,
  useUpdateQueryParams,
  MaterialSymbol,
} from 'vopay';
import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import type { DefaultOptionProps } from 'vopay';
import { format, parseISO, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useHistory } from 'react-router-dom';
import BankVerifyHistoryForm from '@/components/Statements/BankVerifyHistory/BankVerifyHistoryForm';
import BankVerifyHistoryExport from '@/components/Statements/BankVerifyHistory/BankVerifyHistoryExport';
import useFetchAccountVerification from '@/hooks/useApi/useFetchAccountVerification';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import ActivityType from '../ActivityType';
import type { ActivityTabType } from '../ActivityTab';
import { Button, TableFiltersContainer, TransactionStatus } from 'UI';
import useFileDownload from '@/hooks/useFiledDownload';
import { useTranslation } from 'react-i18next';

type QueryParams = {
  status?: string;
  verificationStatus?: string;
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  timeZone?: string;
  activityType?: ActivityTabType;
};

type TransactionParams = {
  status: string;
  verificationStatus: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
  timeZone: string;
  hasInvalidEndDate: boolean;
  offset?: number;
};

type BankVerifyHistoryTableProps = {
  accountID: string;
  activityTypeOptions: DefaultOptionProps[];
  currentType: string;
  handleCurrentType: (val: ActivityTabType) => void;
};

const BankVerifyHistoryTable: FunctionComponent<BankVerifyHistoryTableProps> = ({
  accountID,
  activityTypeOptions,
  currentType,
  handleCurrentType,
}) => {
  const {
    downloadPdfTable,
    downloadCsvFile,
    downloadJson,
    downloadXls,
    downloadDoc,
    downloadTxt,
    downloadXml,
    printFile,
  } = useFileDownload();
  const history = useHistory();
  const { t } = useTranslation('translations', { keyPrefix: 'BankVerifyHistory' });
  const {
    response: { clientAccountsEnabled },
  } = useClientData();
  const accountIDType = clientAccountsEnabled ? 'ClientAccountID' : 'ContactID';
  const alertDialog = useAlertDialog();
  const { userSettings } = useUserSettingsProvider();
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [verificationHistory, , , fetchVerificationHistory] = useFetchAccountVerification({});
  const [verificationHistoryExport, , , fetchVerificationHistoryExport] = useFetchAccountVerification({});
  const [filteredList, setFilteredList] = useState<vp.VerificationRequestsList[]>([]);
  const [columnSwap, setColumnSwap] = useState<'name' | 'email'>('name');

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const timeZone = useMemo(
    () => (queryParams.get('timeZone') as vp.Timezone) || userSettings.timezone || 'UTC',
    [queryParams],
  );

  const initialStartDate = useMemo(() => formatInTimeZone(subDays(new Date(), 7), timeZone, 'yyyy-MM-dd'), [timeZone]);
  const initialEndDate = useMemo(() => formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd'), [timeZone]);

  const status = useMemo(() => queryParams.get('status') || '', [queryParams]);
  const verificationStatus = useMemo(() => queryParams.get('verificationStatus') || '', [queryParams]);
  const startDate = useMemo(() => queryParams.get('startDate') || initialStartDate || '', [queryParams]);
  const endDate = useMemo(() => queryParams.get('endDate') || initialEndDate || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.get('searchTerm') || '', [queryParams]);

  const filtersApplied = useMemo(() => {
    return Boolean(
      status ||
        verificationStatus ||
        searchTerm ||
        (startDate && startDate !== initialStartDate) ||
        (endDate && endDate !== initialEndDate) ||
        (timeZone && timeZone !== (userSettings.timezone || 'UTC')),
    );
  }, [
    status,
    verificationStatus,
    searchTerm,
    startDate,
    endDate,
    timeZone,
    initialStartDate,
    initialEndDate,
    userSettings.timezone,
  ]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  // pagination
  const totalDataLength = useMemo(() => {
    return verificationHistory.PaginationMetaData?.TotalRecords || 0;
  }, [verificationHistory.VerificationRequests]);
  const PER_PAGE = 20;
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const fetchBankVerifyHistory = async ({
    startDate,
    endDate,
    status,
    verificationStatus,
    hasInvalidEndDate,
    searchTerm,
    timeZone,
    offset = 0,
  }: TransactionParams) => {
    const bankVerifyHistoryRequest = {
      StartDateTime: startDate,
      EndDateTime: endDate,
      Timezone: timeZone,
      RequestStatus: status,
      SearchText: searchTerm,
      VerificationStatus: verificationStatus,
      Limit: PER_PAGE,
      Offset: offset,
      [accountIDType]: accountID,
    };

    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);
      return;
    }

    try {
      const res = await fetchVerificationHistory(bankVerifyHistoryRequest);

      if (!res.Success) throw new Error(res.ErrorMessage);
      const verificationRequest = Object.values(res.VerificationRequests || {});
      setFilteredList(verificationRequest);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An error occurred while fetching bank verify history';
      alertDialog.error('Error', msg);
    }
  };

  useEffect(() => {
    const offset = setPageIndex(0);
    fetchBankVerifyHistory({
      startDate,
      endDate,
      status,
      verificationStatus,
      hasInvalidEndDate: false,
      searchTerm,
      timeZone,
      offset,
    });
    updateQueryParams({
      activityType: 'Bank Verify History',
    });
  }, []);

  useEffect(() => {
    setHasInvalidEndDate(startDate > endDate);
  }, [startDate, endDate]);

  const handleExport = async (exportType: 'pdf' | 'csv' | 'xls' | 'doc' | 'text' | 'json' | 'xml' | 'print') => {
    const defaultValues = {
      StartDateTime: initialStartDate,
      EndDateTime: initialEndDate,
      Timezone: timeZone,
      RequestStatus: status,
      SearchText: searchTerm,
      VerificationStatus: verificationStatus,
    };
    const bankVerifyHistoryRequest = defaultValues;
    bankVerifyHistoryRequest.StartDateTime = startDate;
    bankVerifyHistoryRequest.EndDateTime = endDate;
    const exportBankVerifyData = await fetchVerificationHistoryExport(bankVerifyHistoryRequest);
    switch (exportType) {
      case 'pdf':
        downloadPdfTable('export-bank-verify-history-table', t('title'));
        break;
      case 'csv':
        downloadCsvFile('export-bank-verify-history-table', t('title'));
        break;
      case 'xls':
        downloadXls('export-bank-verify-history-table', t('title'));
        break;
      case 'doc':
        downloadDoc('export-bank-verify-history-table', t('title'));
        break;
      case 'text':
        downloadTxt('export-bank-verify-history-table', t('title'));
        break;
      case 'json':
        downloadJson(Object.values(exportBankVerifyData.VerificationRequests || {}), t('title'));
        break;
      case 'xml':
        downloadXml('export-bank-verify-history-table', t('title'));
        break;
      case 'print':
        printFile('export-bank-verify-history-table');
        break;
      default:
        break;
    }
  };

  const tableProps: vp.TableProps<vp.VerificationRequestsList> = {
    id: 'bank-verify-history-table',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'recipientName',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.clientName')}
            <MaterialSymbol icon={'switch_left'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: columnSwap === 'name',
        template: ({ data }) => `${data.Details.Credit.FirstName} ${data.Details.Credit.LastName}`,
        onHeaderClick: () => {
          setColumnSwap('email');
        },
      },
      {
        id: 'recipientEmail',
        header: (
          <Box gap="sm" ai="center">
            {t('Columns.recipientEmail')}
            <MaterialSymbol icon={'switch_right'} fillAmount={1} size={'24px'} />
          </Box>
        ),
        align: 'left',
        visible: columnSwap === 'email',
        template: ({ data }) => data.EmailAddress.Receiver || t('notApplicable'),
        onHeaderClick: () => {
          setColumnSwap('name');
        },
      },
      {
        id: 'account',
        header: t('Columns.account'),
        align: 'left',
        template: ({ data }) => (
          <Box ai="center">
            {data.Details.Bank.FinancialInstitutionId !== null &&
              data.Details.Bank.FinancialInstitutionId !== '' &&
              vp.hasBankImage(data.Details.Bank.FinancialInstitutionId) && (
                <img
                  src={`https://embed-dev.vopay.com/images/logos-banks/${data.Details.Bank.FinancialInstitutionId}.svg`}
                  alt="Bank Logo"
                />
              )}
            <span>{getMaskedNumber(data.Details.Bank.AccountNumber)}</span>
          </Box>
        ),
      },
      {
        id: 'requestStatus',
        header: t('Columns.requestStatus'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(data.Status.Request)}
              label={capitalizeFirstLetter(data.Status.Request) || ''}
            />
          </Box>
        ),
      },
      {
        id: 'verificationStatus',
        header: t('Columns.verificationStatus'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(data.Status.Verification)}
              label={capitalizeFirstLetter(data.Status.Verification) || ''}
            />
          </Box>
        ),
      },
      {
        id: 'updatedOn',
        header: t('Columns.updatedOn'),
        align: 'left',
        field: 'LastModified',
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe">
            {data.Status.Request === 'active' && data.Status.Verification === 'unverified' && (
              <Button
                id={`copy-url-${data.VerificationRequestID}`}
                size="small"
                onClick={() => {
                  void navigator.clipboard.writeText(data.Link);
                }}
              >
                {t('copyUrl')}
              </Button>
            )}
            <Button
              id={`details-${data.VerificationRequestID}`}
              size="small"
              onClick={() => {
                history.push({
                  pathname: `/bank-verify-history/details/${data.VerificationRequestID}`,
                  state: {
                    verification: data,
                  },
                });
              }}
            >
              {t('details')}
            </Button>
          </Box>
        ),
      },
    ],
    rows: filteredList.map((item): { id: string; data: vp.VerificationRequestsList } => ({
      id: String(item.VerificationRequestID || ''),
      data: item,
    })),
  };

  const exportColumns = tableProps.columns.filter((col) => col.id !== 'view');

  const tableExportProps: vp.TableProps<vp.VerificationRequestsList> = {
    ...tableProps,
    id: 'export-bank-verify-history-table',
    columns: exportColumns,
    rows: Object.values(verificationHistoryExport.VerificationRequests || {}).map(
      (item): { id: string; data: vp.VerificationRequestsList } => ({
        id: String(item.VerificationRequestID || ''),
        data: item,
      }),
    ),
  };

  return (
    <Box flx="col">
      <TableFiltersContainer filtersApplied={filtersApplied}>
        <Box jc="sb" ai="center">
          <ActivityType
            activityTypeOptions={activityTypeOptions}
            currentType={currentType}
            handleCurrentType={handleCurrentType}
          />
          <Box ai="center" gap="common">
            <BankVerifyHistoryExport onExport={handleExport} />
            <TableFiltersContainer.Trigger />
          </Box>
        </Box>
        <TableFiltersContainer.Content>
          <Box>
            <BankVerifyHistoryForm
              requestStatus={status}
              timeZone={timeZone}
              verificationStatus={verificationStatus}
              searchTerm={searchTerm}
              hasInvalidEndDate={hasInvalidEndDate}
              shouldShowEndDateError={shouldShowEndDateError}
              setShouldShowEndDateError={setShouldShowEndDateError}
              initialTimeRange={[
                parseISO(startDate || initialStartDate).getTime() as vp.Timestamp,
                parseISO(endDate || initialEndDate).getTime() as vp.Timestamp,
              ]}
              search={(searchParams) => {
                updateQueryParams({
                  status: searchParams.requestStatus,
                  verificationStatus: searchParams.verificationStatus,
                  searchTerm: searchParams.searchTerm,
                  startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                  endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                  timeZone: searchParams.timeZone,
                });

                fetchBankVerifyHistory({
                  startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                  endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                  status: searchParams.requestStatus,
                  verificationStatus: searchParams.verificationStatus,
                  hasInvalidEndDate: searchParams.timeRange[0] > searchParams.timeRange[1],
                  searchTerm: searchParams.searchTerm,
                  timeZone: searchParams.timeZone,
                });
              }}
            />
          </Box>
        </TableFiltersContainer.Content>
      </TableFiltersContainer>
      <div style={{ display: 'none' }}>
        <Table {...tableExportProps} />
      </div>
      <Table {...tableProps} />
      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              const offset = setPageIndex(e.selected);
              fetchBankVerifyHistory({
                startDate,
                endDate,
                status,
                verificationStatus,
                hasInvalidEndDate: startDate > endDate,
                searchTerm,
                timeZone,
                offset,
              });
            }}
          />
        )}
      </Box>

      {tableProps.rows && tableProps.rows.length === 0 && (
        <Box flx="col" jc="center">
          <Box ai="center" jc="center" pt="md">
            {t('noRecordMessage', { defaultValue: 'There are no records to display within this date range' })}
          </Box>
        </Box>
      )}
    </Box>
  );
};
export default BankVerifyHistoryTable;
